import { useCallback, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { EditableComponent } from '@adobe/aem-react-editable-components';
import { MButton, MButtonType } from '@marriott/mi-web-ui-kit';
import { Heading, headingType, size, tags, useCheckBreakpoint } from '@marriott/mi-ui-library';
import { useStore } from '../../modules/store/cobrandStore';
import { StyledComparisonTray } from './CobrandComparisonTray.styles';
import { CompareCard } from './CompareCard';
import { AlertBannerComp } from '../AlertBanner/AlertBanner';
import { CobrandDrawer } from './CobrandDrawer';
import { CobrandComparisonTrayProps } from './CobrandComparisonTray.types';
import { isMobile, useLandscapeOrientation, getMaxCompareCards, restoreCompareSelections } from '../../modules/utils';
import { ComparisonTrayCompareButton } from './ComparisonTrayCompareButton';

const DEFAULT_HEADING_TEXT = 'Cards Selected: {selected} of {max}';
const DEFAULT_DRAWER_LABELS = {
  drawerAriaLabel: 'comparison tray',
  showAriaLabel: 'Show',
  hideAriaLabel: 'Hide',
};

export const CobrandComparisonTrayConfig = {
  emptyLabel: 'Cobrand Comparison Tray',
  isEmpty: () => true,
  resourceType: `/components/content/cobrandcomparisontray`,
};

export const CobrandComparisonTrayComp = (props: CobrandComparisonTrayProps) => {
  const isViewportL = useCheckBreakpoint('viewportL');
  const isViewportM = useCheckBreakpoint('viewportM');
  const isMobileDevice = isMobile();
  const isLandscape = useLandscapeOrientation();
  const [visible, setVisible] = useState(true);

  const isAuthorMode = Boolean(props?.isAuthorMode || props?.isInEditor);
  const { cobrandData, compareTrayAlertVisible, setCobrandData, setCompareTrayAlertVisible } = useStore(store => store);

  const useDesktopLayout = isLandscape || isViewportM;
  const isMobileLandscape = isMobileDevice && isLandscape;
  const maxAllowedCards = useMemo(
    () => getMaxCompareCards(isViewportL && !isMobileDevice),
    [isViewportL, isMobileDevice]
  );
  const slots = useMemo(() => Array.from({ length: maxAllowedCards }, (_, index) => index), [maxAllowedCards]);
  const selectedCards = useMemo(() => cobrandData?.selectedCards ?? [], [cobrandData?.selectedCards]);
  const displayCards = useMemo(() => {
    if (selectedCards.length > 0) {
      const previewMap = new Map((props.authorPreviewCards ?? []).map(card => [card.cardCode, card]));
      return selectedCards.map(card => {
        const preview = previewMap.get(card.cardname);
        if (!preview) return card;
        return {
          ...card,
          header: preview.cardHeader,
          imagePath: preview.imagePath,
          cardType: preview.cardType,
          altText: preview.altText,
        };
      });
    }

    return isAuthorMode ? props.authorPreviewCards ?? [] : [];
  }, [isAuthorMode, props.authorPreviewCards, selectedCards]);

  const selectedCardCount = displayCards.length;
  const headinTextTemplate = props.headingText || DEFAULT_HEADING_TEXT;
  const cardsSelectedText = `${headinTextTemplate}: ${selectedCardCount} of ${maxAllowedCards}`;
  const resolvedAlertMessage =
    isViewportL && !isMobileDevice ? props.desktopLimitExceedMessage ?? '' : props.mobileLimitExceededMessage ?? '';

  const drawerLabels = {
    drawerAriaLabel: props.drawerLabels?.drawerAriaLabel || DEFAULT_DRAWER_LABELS.drawerAriaLabel,
    showAriaLabel: props.drawerLabels?.showAriaLabel || DEFAULT_DRAWER_LABELS.showAriaLabel,
    hideAriaLabel: props.drawerLabels?.hideAriaLabel || DEFAULT_DRAWER_LABELS.hideAriaLabel,
  };

  const isFeatureEnabled = props?.isCardComparisonFeatureEnabled ?? true;
  const shouldRender = isFeatureEnabled && (isAuthorMode || selectedCards.length > 0);
  const shouldForceOpen = compareTrayAlertVisible && !visible;

  useEffect(() => {
    restoreCompareSelections({ setCobrandData });
  }, [setCobrandData]);

  const handleClearAll = useCallback(() => {
    setCobrandData({ ...cobrandData, selectedCards: [] });
  }, [cobrandData, setCobrandData]);

  const handleDrawerChange = useCallback(
    (nextValue: boolean) => {
      setVisible(nextValue);
      if (!nextValue) {
        setCompareTrayAlertVisible(false);
      }
    },
    [setCompareTrayAlertVisible]
  );

  if (!shouldRender) {
    return null;
  }

  return (
    <CobrandDrawer
      variant="drawer"
      placement="bottom"
      initialState={visible}
      labels={drawerLabels}
      title={
        <StyledComparisonTray isCollapsed>
          <div className="container py-2 d-flex flex-row justify-content-between align-items-center">
            <div>
              <Heading
                titleText={cardsSelectedText}
                fontSize={size.large}
                element={tags.h5}
                variation={headingType.subtitle}
              />
            </div>

            <div className="d-flex flex-row align-items-center">
              {!isMobileLandscape && (
                <MButton
                  buttonText="Clear all"
                  buttonType={MButtonType.Tertiary}
                  onClick={handleClearAll}
                  className={clsx('clear-btn', useDesktopLayout ? 'd-block mr-2' : 'd-none')}
                />
              )}
              <div className="ml-1">
                <ComparisonTrayCompareButton
                  compareLinkUrl={props.compareLinkUrl}
                  openInNewTab={props.openInNewTab}
                  targetExperience={props.targetExperience}
                  trackingProperties={props.trackingProperties}
                  disabled={selectedCardCount < 2}
                  compareLinkText={props.compareLinkText}
                />
              </div>
            </div>
          </div>
        </StyledComparisonTray>
      }
      forceOpen={shouldForceOpen}
      onChange={handleDrawerChange}
    >
      <StyledComparisonTray
        data-testid="cobrand-CobrandComparisonTray"
        data-component-name="o-cobrand-CobrandComparisonTray"
      >
        <div className="container pt-4">
          <AlertBannerComp
            title=""
            message={resolvedAlertMessage}
            showError={compareTrayAlertVisible}
            isAuthorMode={isAuthorMode}
            styleclass="comparison-tray-alert"
            paddingClassName="pt-0 pb-3"
          />
          <Heading
            titleText={cardsSelectedText}
            fontSize={size.large}
            element={tags.h5}
            variation={headingType.subtitle}
          />

          <div className={clsx('compare-cards d-flex mt-3', useDesktopLayout ? 'flex-row' : 'flex-column')}>
            <div className={clsx('d-flex', useDesktopLayout ? 'flex-row' : 'flex-column')}>
              {slots.map(index => {
                const cardItem = displayCards[index];

                return (
                  <CompareCard
                    key={index}
                    index={index}
                    item={cardItem}
                    defaultContent={!cardItem}
                    styleClass={clsx(useDesktopLayout ? 'mr-2 mb-0' : 'mb-3')}
                    hideShellSubtitle={isMobileLandscape}
                  />
                );
              })}
            </div>
            <div
              className={clsx(
                'tray-actions',
                useDesktopLayout ? 'd-flex justify-content-end align-items-center' : 'cta-wrapper'
              )}
            >
              {!isMobileLandscape && (
                <MButton
                  buttonText="Clear all"
                  buttonType={MButtonType.Tertiary}
                  onClick={handleClearAll}
                  className={clsx('clear-btn', !isMobileDevice ? 'd-block mr-4' : 'd-none')}
                />
              )}
              <ComparisonTrayCompareButton
                compareLinkUrl={props.compareLinkUrl}
                openInNewTab={props.openInNewTab}
                targetExperience={props.targetExperience}
                trackingProperties={props.trackingProperties}
                disabled={selectedCardCount < 2}
                compareLinkText={props.compareLinkText}
              />
            </div>
          </div>
        </div>
      </StyledComparisonTray>
    </CobrandDrawer>
  );
};

CobrandComparisonTrayComp.displayName = 'CobrandComparisonTrayComp';

export const CobrandComparisonTray = (props: CobrandComparisonTrayProps) => (
  <EditableComponent config={CobrandComparisonTrayConfig} {...props}>
    <CobrandComparisonTrayComp {...props} />
  </EditableComponent>
);
